import React from 'react';

interface ProgressChartProps {
	size?: number;
	strokeWidth?: number;
	progress?: {
		aboveBenchmark: number | undefined;
		atBenchmark: number | undefined;
		belowBenchmark: number | undefined;
		notAssessed: number | undefined;
	};
	iconPath?: string;
}

export const ProgressChart: React.FC<ProgressChartProps> = ({
	size = 200,
	strokeWidth = 8,
	progress = {
		aboveBenchmark: 25,
		atBenchmark: 25,
		belowBenchmark: 25,
		notAssessed: 25
	},
	iconPath
}) => {
	const center = size / 2;
	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const gapSize = 0; // Size of the gap between segments

	// Helper to count non-zero values
	const countNonZeroValues = () => {
		return Object.values(progress).filter((val) => val && val > 0).length;
	};

	// Calculate stroke dasharray and dashoffset for each segment
	const calculateArcValues = (percentage: number, startAngle: number) => {
		const hasMultipleSegments = countNonZeroValues() > 1;
		const arcLength = (percentage / 100) * circumference;
		const rotation = startAngle - 90; // Adjust starting position

		// Only apply gap if there are multiple segments
		const effectiveGapSize = hasMultipleSegments ? gapSize : 0;

		return {
			strokeDasharray: `${arcLength - effectiveGapSize} ${circumference}`,
			transform: `rotate(${rotation}deg)`
		};
	};

	const styles = {
		container: {
			width: size,
			height: size
		}
	};

	const { belowBenchmark = 0, atBenchmark = 0, aboveBenchmark = 0, notAssessed = 0 } = progress;

	const arcValuesAboveBenchmark = calculateArcValues(aboveBenchmark, 0);
	const arcValuesAtBenchmark = calculateArcValues(atBenchmark, (aboveBenchmark * 360) / 100);
	const arcValuesBelowBenchmark = calculateArcValues(belowBenchmark, ((aboveBenchmark + atBenchmark) * 360) / 100);
	const arcValuesNotAssessed = calculateArcValues(
		notAssessed,
		((aboveBenchmark + atBenchmark + belowBenchmark) * 360) / 100
	);

	return (
		<div style={styles.container} className="assessment-progress-chart">
			<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
				{/* background circle */}
				<circle cx={center} cy={center} r={radius} fill="none" stroke="transparent" strokeWidth={strokeWidth} />

				{/* aboveBenchmark segment */}
				{parseInt(arcValuesAboveBenchmark.strokeDasharray) > 0 && (
					<circle
						className="circle-segment above-benchmark"
						cx={center}
						cy={center}
						r={radius}
						fill="none"
						strokeWidth={strokeWidth}
						// strokeLinecap="round"
						style={{
							...arcValuesAboveBenchmark
						}}
					/>
				)}

				{/* atBenchmark segment */}
				{parseInt(arcValuesAtBenchmark.strokeDasharray) > 0 && (
					<circle
						className="circle-segment at-benchmark"
						cx={center}
						cy={center}
						r={radius}
						fill="none"
						strokeWidth={strokeWidth}
						// strokeLinecap="round"
						style={{
							...arcValuesAtBenchmark
						}}
					/>
				)}

				{/* belowBenchmark segment */}
				{parseInt(arcValuesBelowBenchmark.strokeDasharray) > 0 && (
					<circle
						className="circle-segment below-benchmark"
						cx={center}
						cy={center}
						r={radius}
						fill="none"
						strokeWidth={strokeWidth}
						// strokeLinecap="round"
						style={{
							...arcValuesBelowBenchmark
						}}
					/>
				)}

				{/* notAssessed Segment */}
				{parseInt(arcValuesNotAssessed.strokeDasharray) > 0 && (
					<circle
						className="circle-segment not-assessed"
						cx={center}
						cy={center}
						r={radius}
						fill="none"
						strokeWidth={strokeWidth}
						// strokeLinecap="round"
						style={{
							...arcValuesNotAssessed
						}}
					/>
				)}
			</svg>

			{/* Reading Icon */}
			<img src={iconPath} className="icon" alt="reading icon" />
		</div>
	);
};

export default ProgressChart;
